import React, { useState, useRef, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import moment from "moment";

import ReportFilter from "../ReportsGps/ReportFilter";
import ReportLayoutPage from "../ReportsGps/ReportLayoutPage";
import LoadingComponent from "../CommonComponents/loadinComponent";
import { useAttributePreference } from "../CommonComponents/preferences";
import DataTable from "../UserManagement/SubUserManagement/Datatable";

import { FMSReportAction } from "../../actions/FMSReportAction";
import { PermissionAction } from "../../actions/permission";
import "../UserManagement/SubUserManagement/subUser.css";
import { FmsDashboardAction } from "../../actions/Fmsdashboardaction";

// Safe numeric formatter — API sends plain numbers (or 0), never ms.
function fmtNum(val, decimals = 2) {
  if (val === null || val === undefined) return "—";
  return Number(val).toFixed(decimals);
}

function fmtPct(val) {
  if (val === null || val === undefined) return "0%";
  return `${val}%`;
}

const CHIP_PALETTE = {
  gray: { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" },
  dark: { bg: "#f8fafc", color: "#0f172a", border: "#cbd5e1" },
  blue: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  teal: { bg: "#ecfeff", color: "#0e7490", border: "#a5f3fc" },
  green: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  red: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
  amber: { bg: "#fef9c3", color: "#854d0e", border: "#fde68a" },
};

const Chip = ({ children, color = "gray" }) => {
  const isEmpty = children === "—" || children === "NA" || children === null || children === undefined;
  const p = CHIP_PALETTE[isEmpty ? "gray" : color] || CHIP_PALETTE.gray;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: p.bg,
        color: p.color,
        border: `1px solid ${p.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cell — base text style for every body cell (overrides DataTable's default
// styling, which otherwise renders larger/blue text).
// ─────────────────────────────────────────────────────────────────────────────
const cellTextStyle = { color: "#374151", fontSize: 13 };

const Cell = ({ children, style }) => (
  <span style={{ ...cellTextStyle, ...style }}>{children}</span>
);

// Any negative deviation value (across Deviation Ltr/Mileage/%/KM/Engine
// Hours) gets a light-red highlight. Works whether the value is a plain
// negative number (deviationInKM) or a pre-formatted string that carries its
// own minus sign, e.g. "-5.00" or "-4 Hr -57 Min".
const isNegativeVal = (val) => {
  if (val === null || val === undefined) return false;
  if (String(val).trim().startsWith("-")) return true;
  const num = Number(val);
  return !Number.isNaN(num) && num < 0;
};

const negativeCellStyle = {
  background: "#FFF1F1",
  color: "#B91C1C",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 4,
};

const DeviationCell = ({ value }) => (
  <Cell style={isNegativeVal(value) ? negativeCellStyle : undefined}>{value}</Cell>
);

// ─────────────────────────────────────────────────────────────────────────────
// COLUMN META — static id/label pairs only. Used for the "Columns" toggle
// dropdown, localStorage visibility defaults, and export label headers —
// none of which need access to component state, so this can stay outside
// the component and be shared by both FuelSummaryReport and Filter.
// The actual render functions (built in buildColumns below) live inside the
// component because a few of them — View Fuel Graph in particular — need to
// close over component state (reportDates from the current filter).
// ─────────────────────────────────────────────────────────────────────────────
const COLUMN_META = [
  { id: "sno", label: "S.No." },
  { id: "vehicleNo", label: "Vehicle No." },
  { id: "vehicleType", label: "Vehicle Type" },
  { id: "area", label: "Area" },
  { id: "running", label: "Running (Kms.)" },
  { id: "engineHours", label: "Engine Hours" },
  { id: "expectedLtr", label: "Expected (Ltr.)" },
  { id: "actualLtr", label: "Actual (Ltr.)" },
  { id: "deviationLtr", label: "Deviation (Ltr.)" },
  { id: "expMileage", label: "Exp. Mileage" },
  { id: "actMileage", label: "Act. Mileage" },
  { id: "deviationInMileage", label: "Deviation In Mileage" },
  { id: "deviationPercentage", label: "Deviation %age" },
  { id: "fuelAtStart", label: "Fuel At Start(Ltr.)" },
  { id: "fuelAtEnd", label: "Fuel At End(Ltr.)" },
  { id: "fuelFill", label: "Fuel Fill(Ltr.)" },
  { id: "actualFuelDrop", label: "Actual Fuel Drop(Ltr.)" },
  { id: "expectedFuelDrop", label: "Expected Fuel Drop(Ltr.)" },
  { id: "missedFuelFill", label: "Missed Fuel Fill(Ltr.)" },
  { id: "startOdometer", label: "Start Odometer" },
  { id: "endOdometer", label: "End Odometer" },
  { id: "idleHours", label: "Idle Hours" },
  { id: "expectedWorkingHours", label: "Expected Working Hours" },
  { id: "workPercentage", label: "Work %age" },
  { id: "lowPowerMode", label: "Low Power Mode" },
  { id: "mediumPowerMode", label: "Medium Power Mode" },
  { id: "highPowerMode", label: "High Power Mode" },
  { id: "utilizationExpectedKM", label: "Utilization Expected In KM" },
  { id: "utilizationExpectedEngineHours", label: "Utilization Expected In Engine Hours" },
  { id: "deviationInKM", label: "Deviation In KM" },
  { id: "deviationInEngineHours", label: "Deviation In Engine Hours" },
  { id: "model", label: "Model" },
  { id: "registrationYear", label: "Registration Year" },
  { id: "maker", label: "Maker" },
  { id: "viewFuelGraph", label: "View Fuel Graph" },
];

const colSettingsKey = "fuel_summary_report_col_settings";

const defaultVisibleColumns = COLUMN_META.reduce((acc, col) => {
  acc[col.id] = true;
  return acc;
}, {});

// Builds the full column list (id + label + render) for the current render
// pass. `reportDates` is the date range currently applied via the filter —
// View Fuel Graph opens the history page for that same range, since a fuel
// summary row has no per-row start/end time of its own (unlike a trip row).
function buildColumns({ pageNo, pageSize, reportDates }) {
  const renderers = {
    sno: (_row, i) => <Cell>{i + 1 + (pageNo || 0) * (pageSize || 0)}</Cell>,
    vehicleNo: (row) => <Cell style={{ fontWeight: 600 }}>{row.vehicleNo}</Cell>,
    vehicleType: (row) => <Cell>{row.vehicleType}</Cell>,
    area: (row) => <Cell>{row.area}</Cell>,
    running: (row) => <Cell>{row.running}</Cell>,
    engineHours: (row) => <Cell>{row.engineHours}</Cell>,
    expectedLtr: (row) => <Cell>{row.expectedLtr}</Cell>,
    actualLtr: (row) => <Cell>{row.actualLtr}</Cell>,
    deviationLtr: (row) => <DeviationCell value={row.deviationLtr} />,
    expMileage: (row) => <Cell>{row.expMileage}</Cell>,
    actMileage: (row) => <Cell>{row.actMileage}</Cell>,
    deviationInMileage: (row) => <DeviationCell value={row.deviationInMileage} />,
    deviationPercentage: (row) => <DeviationCell value={row.deviationPercentage} />,
    fuelAtStart: (row) => <Cell>{row.fuelAtStart}</Cell>,
    fuelAtEnd: (row) => <Cell>{row.fuelAtEnd}</Cell>,
    fuelFill: (row) => <Cell>{row.fuelFill}</Cell>,
    actualFuelDrop: (row) => <Cell>{row.actualFuelDrop}</Cell>,
    expectedFuelDrop: (row) => <Cell>{row.expectedFuelDrop}</Cell>,
    missedFuelFill: (row) => <Cell>{row.missedFuelFill}</Cell>,
    startOdometer: (row) => <Cell>{row.startOdometer}</Cell>,
    endOdometer: (row) => <Cell>{row.endOdometer}</Cell>,
    idleHours: (row) => <Cell>{row.idleHours}</Cell>,
    expectedWorkingHours: (row) => <Cell>{row.expectedWorkingHours}</Cell>,
    workPercentage: (row) => <Cell>{row.workPercentage}</Cell>,
    lowPowerMode: (row) => <Cell>{row.lowPowerMode}</Cell>,
    mediumPowerMode: (row) => <Cell>{row.mediumPowerMode}</Cell>,
    highPowerMode: (row) => <Cell>{row.highPowerMode}</Cell>,
    utilizationExpectedKM: (row) => <Cell>{row.utilizationExpectedKM}</Cell>,
    utilizationExpectedEngineHours: (row) => <Cell>{row.utilizationExpectedEngineHours}</Cell>,
    deviationInKM: (row) => <DeviationCell value={row.deviationInKM} />,
    deviationInEngineHours: (row) => <DeviationCell value={row.deviationInEngineHours} />,
    model: (row) => <Cell>{row.model}</Cell>,
    registrationYear: (row) => <Cell>{row.registrationYear}</Cell>,
    maker: (row) => <Cell>{row.maker}</Cell>,
    viewFuelGraph: (row) => (
      <div
        onClick={(e) => {
          e.stopPropagation();
          localStorage.setItem("vehicleNumber", JSON.stringify(row.vehicleNo || ""));
          if (reportDates.from) {
            localStorage.setItem("historyFromDate", new Date(reportDates.from));
          }
          if (reportDates.to) {
            localStorage.setItem("historyToDate", new Date(reportDates.to));
          }

          localStorage.setItem("historyOpenPanel", "fuel");
          window.open(`/history/${row.gpsRefId}`, "_blank");
        }}
        style={{ cursor: "pointer", display: "inline-flex" }}
      >
        <span
          style={{
            ...cellTextStyle,
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 12px",
            borderRadius: 999,
            fontWeight: 700,
            background: "#082D6B",
            color: "#ffffff",
          }}
        >
          View
        </span>
      </div>
    ),
  };

  return COLUMN_META.map((meta) => ({ ...meta, render: renderers[meta.id] }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTER — pure UI: validates dates, hands the search off to the parent,
// which owns fetching so both the Show button and DataTable's pagination
// controls can trigger the same request.
// ─────────────────────────────────────────────────────────────────────────────
const Filter = ({ onSearch, setReportType, extraActions, exportData }) => {
  const handleSubmit = (deviceId, from, to) => {
    const fromDate = moment(from);
    const toDate = moment(to);

    if (toDate.isBefore(fromDate)) {
      alert("To Date cannot be less than From Date");
      return;
    }
    if (toDate.diff(fromDate, "months", true) > 1) {
      alert("Date difference should not be more than 1 month");
      return;
    }

    onSearch(deviceId, from, to);
  };

  const labelHeader = COLUMN_META.map((c) => c.label);

  return (
    <ReportFilter
      handleSubmit={handleSubmit}
      setReportType={setReportType}
      reportType="fmsFuelSummary"
      exportData={exportData}
      labelHeader={labelHeader}
      extraActions={extraActions}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
const FuelSummaryReport = (props) => {
  const { setReportType } = props;
  useAttributePreference("distanceUnit");

  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [reportDates, setReportDates] = useState({ from: null, to: null });
  const [summaryTabs, setSummaryTabs] = useState([]);

  // ── Pagination (server-side, matches the FMS endpoint's pageNo/pageSize) ──
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const lastFilterRef = useRef({ deviceIds: [], from: null, to: null });

  const API_DATE_FORMAT = "DD/MM/YYYY HH:mm";

  const buildSummaryTabs = (d) => {
    if (!d) return [];
    const n = (v) => (v == null ? "—" : Number(v).toFixed(2));
    return [
      { name: "Vehicles", value: d.tVehicles ?? "—", color: "blue" },
      { name: "Total Km", value: n(d.tKm), color: "teal" },
      { name: "Engine Hours", value: d.tEngineHours ?? "—", color: "teal" },
      { name: "Expected (L)", value: n(d.expectedConsumption), color: "gray" },
      { name: "Actual (L)", value: n(d.actualConsumption), color: "gray" },
      { name: "Deviation (L)", value: n(d.deviation), color: d.deviation < 0 ? "red" : "green" },
      { name: "Fuel Fill (L)", value: n(d.tFuelFill), color: "green" },
      { name: "Fuel Drop (L)", value: n(d.tFuelDropage), color: d.tFuelDropage > 0 ? "red" : "gray" },
      { name: "Confirmed Drop (L)", value: n(d.confirmedDropage), color: "amber" },
      { name: "Unchecked Drop (L)", value: n(d.unattendedDropage), color: "amber" },
      { name: "Fill Alerts", value: d.alerts?.fuelFill?.total ?? 0, color: "green" },
      { name: "Drop Alerts", value: d.alerts?.fuelDrop?.total ?? 0, color: "red" },
      { name: "Est. Loss (₹)", value: n(d.droppage?.estimatedLoss), color: "red" },
    ];
  };

  const fmtHrs = (h) => {
    if (h === null || h === undefined) return "—";
    const neg = Number(h) < 0;
    const mins = Math.round(Math.abs(Number(h)) * 60);
    return `${neg ? "-" : ""}${Math.floor(mins / 60)} Hr ${mins % 60} Min`;
  };

  const fmtPctOrDash = (v) => (v === null || v === undefined ? "—" : `${v}%`);

  const mapSummaryRows = (list) =>
    list.map((v, idx) => ({
      _idx: idx,
      gpsRefId: v.vehicleId,

      vehicleNo: v.vehicleNo || "—",
      vehicleType: v.vehicleType || "—",
      area: v.area || "—",

      running: fmtNum(v.runningKms),
      engineHours: fmtHrs(v.engineHours),
      expectedLtr: fmtNum(v.expectedL),
      actualLtr: fmtNum(v.actualL),
      deviationLtr: fmtNum(v.deviationL),
      expMileage: fmtNum(v.expMileage),
      actMileage: fmtNum(v.actMileage),
      deviationInMileage: fmtNum(v.deviationMileage),
      deviationPercentage: fmtPctOrDash(v.deviationPct),

      fuelAtStart: fmtNum(v.fuelAtStart),
      fuelAtEnd: fmtNum(v.fuelAtEnd),
      fuelFill: fmtNum(v.fuelFill),
      actualFuelDrop: fmtNum(v.actualFuelDrop),
      expectedFuelDrop: fmtNum(v.expectedFuelDrop),
      missedFuelFill: fmtNum(v.missedFuelFill),

      startOdometer: fmtNum(v.startOdometer),
      endOdometer: fmtNum(v.endOdometer),
      idleHours: fmtHrs(v.idleHours),
      expectedWorkingHours: fmtHrs(v.expectedWorkingHours),
      workPercentage: fmtPctOrDash(v.workPct),

      lowPowerMode: v.lowPowerMode ?? "NA",
      mediumPowerMode: v.mediumPowerMode ?? "NA",
      highPowerMode: v.highPowerMode ?? "NA",

      utilizationExpectedKM: fmtNum(v.utilizationExpectedKm),
      utilizationExpectedEngineHours: fmtHrs(v.utilizationExpectedEngineHours),
      deviationInKM: fmtNum(v.deviationKm),
      deviationInEngineHours: fmtHrs(v.deviationEngineHours),

      model: v.model || "—",
      registrationYear: v.registrationYear || "—",
      maker: v.maker || "—",
    }));


  const fetchReport = async (deviceIds, from, to, page = 0, size = pageSize) => {
    setLoading(true);
    try {
      const fromDate = moment(from).format(API_DATE_FORMAT);
      const toDate = moment(to).format(API_DATE_FORMAT);

      const [ table] = await Promise.all([
        FMSReportAction.fetchFmsFuelSummaryReport({
          fromDate,
          toDate,
          vehicleIds: deviceIds,
          page,
          size,
        }),
      ]);

      setReportDates({ from, to });
      // setSummaryTabs(buildSummaryTabs(summary));

      const rows = mapSummaryRows(table?.content || []);
      setItems(rows);
      setExportData(rows);
      setTotalCount(table?.totalElements ?? rows.length);
      setPageNo(page);
      setPageSize(size);
    } catch (err) {
      console.error("Error fetching FMS fuel summary report:", err);
      setItems([]);
      setExportData([]);
      setSummaryTabs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (deviceId, from, to) => {
    let deviceIds = [];
    if (Array.isArray(deviceId)) {
      deviceIds = deviceId.map((v) => v);
    } else if (deviceId) {
      deviceIds = [deviceId];
    }
    lastFilterRef.current = { deviceIds, from, to };
    fetchReport(deviceIds, from, to, 0, pageSize);
  };

  const handleChangePage = (event, newPage) => {
    const { deviceIds, from, to } = lastFilterRef.current;
    if (!from || !to) return;
    fetchReport(deviceIds, from, to, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    const { deviceIds, from, to } = lastFilterRef.current;
    if (!from || !to) {
      setPageSize(newSize);
      return;
    }
    fetchReport(deviceIds, from, to, 0, newSize);
  };

  // ── Column visibility ──
  const [headerConfigs, setHeaderConfigs] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(colSettingsKey);
    return saved ? { ...defaultVisibleColumns, ...JSON.parse(saved) } : defaultVisibleColumns;
  });
  const [showColSettings, setShowColSettings] = useState(false);
  const colSettingsRef = useRef(null);

  const fetchHeaderVisibility = () => {
    const stored = JSON.parse(localStorage.getItem("data"));
    if (stored && stored.id) {
      PermissionAction.getTableHeadersVisibility(
        "FUEL SUMMARY REPORT PAGE",
        stored.id,
      ).then((res) => {
        if (res && res.data && Array.isArray(res.data)) {
          setHeaderConfigs(res.data);
          const apiVisibility = {};
          res.data.forEach((item) => {
            apiVisibility[item.headerName] = item.isVisible;
          });
          setVisibleColumns((prev) => ({ ...prev, ...apiVisibility }));
        }
      });
    }
  };

  useEffect(() => {
    fetchHeaderVisibility();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
        setShowColSettings(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleColumn = async (colId) => {
    const nextVal = !visibleColumns[colId];
    setVisibleColumns((prev) => {
      const next = { ...prev, [colId]: nextVal };
      localStorage.setItem(colSettingsKey, JSON.stringify(next));
      return next;
    });

    const stored = JSON.parse(localStorage.getItem("data"));
    if (stored && stored.id && headerConfigs.length > 0) {
      const updatedConfigs = headerConfigs.map((cfg) =>
        cfg.headerName === colId ? { ...cfg, isVisible: nextVal } : cfg,
      );
      await PermissionAction.updateTableHeadersVisibility(updatedConfigs);
      fetchHeaderVisibility();
    }
  };

  const allColumns = buildColumns({ pageNo, pageSize, reportDates });
  const visibleColumnDefs = allColumns.filter((col) => visibleColumns[col.id]);

  return (
    <div className="sub-user-list-root" style={{ position: "relative" }}>
      <LoadingComponent isLoading={isLoading} />

      <Container style={{ marginTop: "-2rem" }}>
        <ReportLayoutPage
          filter={
            <Filter
              onSearch={handleSearch}
              setReportType={setReportType}
              exportData={exportData}
              extraActions={
                <div className="gf-col-settings-wrap" ref={colSettingsRef}>
                  <button
                    className={`gf-col-settings-btn ${showColSettings ? "active" : ""}`}
                    onClick={() => setShowColSettings(!showColSettings)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      height: 40,
                      padding: "0 16px",
                      border: "none",
                      borderRadius: 10,
                      background: showColSettings
                        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)"
                        : "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)",
                      color: "#ffffff",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      boxShadow: showColSettings
                        ? "0 6px 24px rgba(30, 41, 59, 0.55)"
                        : "0 4px 16px rgba(30, 41, 59, 0.4)",
                      letterSpacing: "0.3px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showColSettings ? "translateY(-1px)" : "translateY(0)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)";
                      e.currentTarget.style.boxShadow = "0 6px 24px rgba(30, 41, 59, 0.55)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      if (!showColSettings) {
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(30, 41, 59, 0.4)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Columns
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                      <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {showColSettings && (
                    <div className="gf-col-dropdown">
                      <div className="gf-col-dropdown-header">Toggle Columns</div>
                      {COLUMN_META.map((col) => (
                        <label key={col.id} className="gf-col-dropdown-item">
                          <input
                            type="checkbox"
                            className="gf-col-checkbox"
                            checked={!!visibleColumns[col.id]}
                            onChange={() => toggleColumn(col.id)}
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
          }
        >
          {/* {summaryTabs.length > 0 && (
            <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
              {summaryTabs.map((tab, i) => (
                <Grid item key={i}>
                  <div
                    style={{
                      background: tab.bgColor,
                      color: tab.fontColor,
                      border: tab.border,
                      borderRadius: 6,
                      padding: "8px 16px",
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.name}: {tab.value}
                  </div>
                </Grid>
              ))}
            </Grid>
          )} */}

          <Paper className="sub-user-list-paper">
            <DataTable
              columns={visibleColumnDefs}
              rows={items}
              rowKey={(row) => row._idx}
              tableId="fuel-summary-report-table"
              page={pageNo}
              rowsPerPage={pageSize}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
              showRowMenu={false}
            />
          </Paper>
        </ReportLayoutPage>
      </Container>
    </div>
  );
};

export default FuelSummaryReport;